// Editor della VETRINA AVANZATA (negozio personale, piano Pro).
// Schede: Contenuti (gli stessi della vetrina base) · Aspetto · Menu · In evidenza,
// con anteprima dal vivo e un unico pulsante di salvataggio.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowDown, ArrowUp, Check, Copy, Crown, ExternalLink, Link2, FileText, Image as ImageIcon, Loader2,
  Lock, MapPin, Palette, ListOrdered, Sparkles, Star, Truck, Upload, X, Layers,
} from 'lucide-react';
import {
  CUSTOM_TIMES, DISPATCH_OPTIONS, POLICY_TEXT_LIMIT, RETURN_RULES, clearShopShippingCache, describeShipping, loadShopShipping,
} from '../../lib/shopPolicies';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { supabase } from '../../lib/supabase';
import { optimizeImageUrl } from '../../utils/imageUrl';
import { saveMyShop } from '../../lib/shopsApi';
import { shopShortUrl } from '../../lib/shopSession';
import ShopQrCode from './ShopQrCode';
import ShopCollectionsEditor from './ShopCollectionsEditor';
import {
  MAX_FEATURED, SHOP_LAYOUTS, SHOP_MENU_ITEMS, SHOP_PALETTES,
  ensureShopFont, getPalette, layoutClasses, normalizeMenu, shopModeStyle, shopThemeStyle,
} from '../../lib/shopThemes';

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

const EMPTY_CONTACT = { visibility: 'hidden', address: '', postal_code: '', city: '', province: '', visits: 'none', hours: '', note: '' };

const VISIBILITY_OPTIONS = [
  { id: 'hidden', title: 'Non mostrare', text: 'Nessuna indicazione sul luogo' },
  { id: 'city', title: 'Solo la città', text: 'Consigliato se lavori da casa' },
  { id: 'address', title: 'Indirizzo e mappa', text: 'Per laboratori e negozi aperti al pubblico' },
];

const VISIT_OPTIONS = [
  { id: 'none', label: 'Niente visite' },
  { id: 'appointment', label: 'Su appuntamento' },
  { id: 'open', label: 'Aperto al pubblico' },
];

const TABS = [
  { id: 'contenuti', label: 'Contenuti', icon: FileText },
  { id: 'aspetto', label: 'Aspetto', icon: Palette },
  { id: 'menu', label: 'Menu', icon: ListOrdered },
  { id: 'evidenza', label: 'In evidenza', icon: Sparkles },
  { id: 'collezioni', label: 'Collezioni', icon: Layers },
  { id: 'contatti', label: 'Dove siamo', icon: MapPin },
  { id: 'spedizioni', label: 'Spedizioni e resi', icon: Truck },
];

export default function ShopCustomizer({
  shop,
  onSaved,
  onSaveContents,
  contentFields,
  userId,
  slug,
  storefrontTitle,
  storefrontMotto,
  coverImage,
  sectionName,
  products = [],
}) {
  const [tab, setTab] = useState('contenuti');
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [branding, setBranding] = useState(shop.branding);
  const [menu, setMenu] = useState(shop.menu);
  const [featured, setFeatured] = useState(shop.featured_product_ids);
  const [contact, setContact] = useState(shop.contact || EMPTY_CONTACT);
  const [policies, setPolicies] = useState(shop.policies || {});
  const [collections, setCollections] = useState(shop.collections || []);
  const [shipping, setShipping] = useState(null);

  // Riallinea se la configurazione salvata cambia (es. dopo un salvataggio)
  useEffect(() => {
    setBranding(shop.branding);
    setMenu(shop.menu);
    setFeatured(shop.featured_product_ids);
    setContact(shop.contact || EMPTY_CONTACT);
    setPolicies(shop.policies || {});
    setCollections(shop.collections || []);
  }, [shop]);

  // Tariffe di spedizione (si gestiscono nella sezione Spedizioni della dashboard)
  useEffect(() => {
    if (tab !== 'spedizioni' || !userId) return undefined;
    let active = true;
    clearShopShippingCache();
    loadShopShipping(userId).then((d) => active && setShipping(d));
    return () => { active = false; };
  }, [tab, userId]);

  // Caratteri dei modelli: tutti quando si sceglie il modello, altrimenti solo quello in uso
  useEffect(() => {
    if (tab === 'aspetto') SHOP_LAYOUTS.forEach((l) => ensureShopFont(l.id));
    else ensureShopFont(branding.layout);
  }, [tab, branding.layout]);

  const live = shop.is_published;
  const activeProducts = products.filter((p) => !p.status || p.status === 'active');

  const moveMenu = (index, delta) => {
    const next = [...menu];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setMenu(next);
  };

  const toggleMenu = (key) => {
    if (SHOP_MENU_ITEMS[key]?.required) return;
    setMenu(menu.map((m) => (m.key === key ? { ...m, enabled: !m.enabled } : m)));
  };

  const toggleFeatured = (pid) => {
    if (featured.includes(pid)) {
      setFeatured(featured.filter((id) => id !== pid));
    } else if (featured.length >= MAX_FEATURED) {
      toast.error(`Puoi mettere in evidenza al massimo ${MAX_FEATURED} prodotti`);
    } else {
      setFeatured([...featured, pid]);
    }
  };

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      toast.error('Il logo deve essere un\'immagine PNG, JPG o WebP');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error('Il logo non può superare i 2 MB');
      return;
    }
    setUploadingLogo(true);
    try {
      const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
      const path = `shops/${userId}/logo-${Date.now()}.${ext}`;
      // Nome sempre nuovo e upsert disattivato: lo storage concede solo l'inserimento
      const { error } = await supabase.storage
        .from('rarifex-uploads')
        .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
      if (error) throw error;
      const { data } = supabase.storage.from('rarifex-uploads').getPublicUrl(path);
      setBranding({ ...branding, logo_url: data.publicUrl });
      toast.success('Logo caricato: ricordati di salvare');
    } catch (err) {
      console.error('Logo upload error:', err);
      toast.error(`Caricamento del logo non riuscito${err?.message ? `: ${err.message}` : ''}`);
    } finally {
      setUploadingLogo(false);
    }
  };

  const save = async (publish) => {
    const unnamed = collections.find((c) => (c.title || '').trim().length < 2);
    if (unnamed) {
      setTab('collezioni');
      toast.error('Dai un nome a ogni collezione prima di salvare');
      return;
    }
    setSaving(true);
    try {
      const contentsOk = await onSaveContents();
      if (!contentsOk) return;
      const res = await saveMyShop({
        branding,
        menu,
        featured_product_ids: featured,
        contact,
        policies,
        collections: collections.map(({ id, title, description, product_ids, cover_product_id, visible }) => ({
          id, title, description, product_ids, cover_product_id, visible,
        })),
        is_published: publish,
      });
      onSaved(res);
      if (res.warning) toast.warning(res.warning);
      toast.success(publish ? 'Vetrina avanzata pubblicata' : 'Vetrina avanzata salvata (non ancora visibile ai clienti)');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Anteprima nella modalità negozio (sito della bottega) e link da condividere
  const previewUrl = `/artigiano/${slug || userId}?negozio=1&anteprima=1`;
  const shareUrl = slug ? `${window.location.origin}/artigiano/${slug}?ref=${slug}` : null;
  // Link breve <sito>/@<slug> (middleware.js)
  const shortUrl = shopShortUrl(slug);
  const copyShareUrl = async (text = shareUrl) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copiato');
    } catch (_) {
      toast.error('Copia non riuscita: seleziona il link e copialo a mano');
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6" data-testid="shop-customizer">
      {/* Impostazioni */}
      <div className="xl:col-span-3 space-y-4 min-w-0">
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg overflow-x-auto" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`shop-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'contenuti' && (
          <div className="space-y-6">
            <p className="text-sm text-slate-500">
              Sono gli stessi contenuti della vetrina base: se torni alla base li ritrovi uguali.
            </p>
            {contentFields}
          </div>
        )}

        {tab === 'aspetto' && (
          <Card className="p-6 space-y-8">
            {/* Logo */}
            <section>
              <Label className="flex items-center gap-2 mb-3"><ImageIcon className="w-4 h-4 text-[#008080]" /> Logo della bottega</Label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-xl border bg-white flex items-center justify-center overflow-hidden">
                  {branding.logo_url ? (
                    <img src={optimizeImageUrl(branding.logo_url, 200)} alt="Logo" className="w-full h-full object-contain p-1.5" />
                  ) : (
                    <ImageIcon className="w-7 h-7 text-slate-300" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex">
                    <input type="file" accept={LOGO_TYPES.join(',')} className="hidden" onChange={handleLogo} disabled={uploadingLogo} data-testid="shop-logo-input" />
                    <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm cursor-pointer hover:bg-slate-50">
                      {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {branding.logo_url ? 'Cambia logo' : 'Carica logo'}
                    </span>
                  </label>
                  {branding.logo_url && (
                    <button type="button" onClick={() => setBranding({ ...branding, logo_url: null })} className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1">
                      <X className="w-3 h-3" /> Rimuovi (si torna alla foto profilo)
                    </button>
                  )}
                  <p className="text-xs text-slate-500">PNG, JPG o WebP, max 2 MB. Meglio quadrato e con sfondo chiaro.</p>
                </div>
              </div>
            </section>

            {/* Palette */}
            <section>
              <Label className="flex items-center gap-2 mb-3"><Palette className="w-4 h-4 text-[#008080]" /> Colori</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {SHOP_PALETTES.map((p) => {
                  const selected = branding.palette === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setBranding({ ...branding, palette: p.id })}
                      className={`rounded-xl border-2 p-2.5 text-left transition-all ${selected ? 'border-slate-800 shadow-md' : 'border-slate-200 hover:border-slate-400'}`}
                      data-testid={`shop-palette-${p.id}`}
                      aria-pressed={selected}
                    >
                      <div className="flex h-8 rounded-md overflow-hidden">
                        <span className="flex-[3]" style={{ background: p.primary }} />
                        <span className="flex-1" style={{ background: p.accent }} />
                        <span className="flex-1 border-l" style={{ background: p.bg }} />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs font-medium text-slate-700">
                        {p.label}
                        {selected && <Check className="w-3.5 h-3.5" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Layout */}
            <section>
              <Label className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-[#008080]" /> Modello grafico</Label>
              <p className="text-xs text-slate-500 mb-3">Cambia impaginazione, caratteri e forme di tutto il negozio. Guarda l'effetto nell'anteprima.</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="shop-layout-picker">
                {SHOP_LAYOUTS.map((l) => {
                  const selected = branding.layout === l.id;
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setBranding({ ...branding, layout: l.id })}
                      className={`rounded-xl border-2 p-3 text-left transition-all ${selected ? 'border-slate-800 shadow-md' : 'border-slate-200 hover:border-slate-400'}`}
                      data-testid={`shop-layout-${l.id}`}
                      aria-pressed={selected}
                    >
                      <LayoutSketch layout={l.id} palette={getPalette(branding.palette)} />
                      <p className="mt-2 flex items-center justify-between gap-1 text-slate-800">
                        <span className={`truncate text-[13px] ${shopModeStyle(l.id).heading} normal-case !tracking-normal`}>{l.label}</span>
                        {selected && <Check className="w-4 h-4 shrink-0" />}
                      </p>
                      <p className="text-[11px] leading-snug text-slate-500 mt-0.5">{l.description}</p>
                    </button>
                  );
                })}
              </div>
            </section>
          </Card>
        )}

        {tab === 'menu' && (
          <Card className="p-6">
            <p className="text-sm text-slate-500 mb-3">
              L'ordine del menu è anche l'ordine delle sezioni nella pagina. Prodotti e Recensioni restano sempre visibili.
            </p>
            <ul className="divide-y rounded-xl border" data-testid="shop-menu-editor">
              {menu.map((m, i) => {
                const def = SHOP_MENU_ITEMS[m.key];
                const label = m.key === 'laboratorio' ? sectionName || def.label : def.label;
                return (
                  <li key={m.key} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="flex flex-col">
                      <button type="button" onClick={() => moveMenu(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-800 disabled:opacity-30" aria-label={`Sposta ${label} in alto`}>
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => moveMenu(i, 1)} disabled={i === menu.length - 1} className="text-slate-400 hover:text-slate-800 disabled:opacity-30" aria-label={`Sposta ${label} in basso`}>
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${m.enabled ? 'text-slate-800' : 'text-slate-400'}`}>{label}</p>
                      {def.hint && <p className="text-xs text-slate-500">{def.hint}</p>}
                    </div>
                    {def.required ? (
                      <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Sempre</span>
                    ) : (
                      <Switch checked={m.enabled} onCheckedChange={() => toggleMenu(m.key)} aria-label={`Mostra ${label}`} />
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        )}

        {tab === 'contatti' && (
          <Card className="p-6 space-y-6" data-testid="shop-contact-editor">
            <div>
              <Label className="flex items-center gap-2 mb-1"><MapPin className="w-4 h-4 text-[#008080]" /> Dove si trova la bottega</Label>
              <p className="text-xs text-slate-500 mb-3">Compare nella pagina Contatti, nel piè di pagina e, con le visite attive, anche in home con la mappa.</p>
              <div className="grid sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Cosa mostrare">
                {VISIBILITY_OPTIONS.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role="radio"
                    aria-checked={contact.visibility === o.id}
                    onClick={() => setContact({ ...contact, visibility: o.id })}
                    className={`rounded-xl border-2 p-3 text-left ${contact.visibility === o.id ? 'border-slate-800 shadow-sm' : 'border-slate-200 hover:border-slate-400'}`}
                    data-testid={`shop-contact-visibility-${o.id}`}
                  >
                    <p className="text-sm font-semibold text-slate-800">{o.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{o.text}</p>
                  </button>
                ))}
              </div>
            </div>

            {contact.visibility !== 'hidden' && (
              <>
                <div className="grid sm:grid-cols-6 gap-3">
                  {contact.visibility === 'address' && (
                    <div className="sm:col-span-6">
                      <Label htmlFor="shop-address">Via e numero civico</Label>
                      <input id="shop-address" value={contact.address} maxLength={150} onChange={(e) => setContact({ ...contact, address: e.target.value })} placeholder="Es. Via Roma 12" className="mt-1 w-full h-10 px-3 rounded-md border text-sm" />
                    </div>
                  )}
                  {contact.visibility === 'address' && (
                    <div className="sm:col-span-2">
                      <Label htmlFor="shop-cap">CAP</Label>
                      <input id="shop-cap" value={contact.postal_code} maxLength={10} onChange={(e) => setContact({ ...contact, postal_code: e.target.value })} placeholder="24121" className="mt-1 w-full h-10 px-3 rounded-md border text-sm" />
                    </div>
                  )}
                  <div className={contact.visibility === 'address' ? 'sm:col-span-3' : 'sm:col-span-4'}>
                    <Label htmlFor="shop-city">Città</Label>
                    <input id="shop-city" value={contact.city} maxLength={80} onChange={(e) => setContact({ ...contact, city: e.target.value })} placeholder="Bergamo" className="mt-1 w-full h-10 px-3 rounded-md border text-sm" />
                  </div>
                  <div className={contact.visibility === 'address' ? 'sm:col-span-1' : 'sm:col-span-2'}>
                    <Label htmlFor="shop-prov">Prov.</Label>
                    <input id="shop-prov" value={contact.province} maxLength={40} onChange={(e) => setContact({ ...contact, province: e.target.value.toUpperCase() })} placeholder="BG" className="mt-1 w-full h-10 px-3 rounded-md border text-sm" />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Visite in laboratorio</Label>
                  <div className="flex flex-wrap gap-2">
                    {VISIT_OPTIONS.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setContact({ ...contact, visits: o.id })}
                        className={`h-9 px-4 rounded-full border text-sm ${contact.visits === o.id ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                {contact.visits !== 'none' && (
                  <div>
                    <Label htmlFor="shop-hours">Orari</Label>
                    <textarea id="shop-hours" value={contact.hours} maxLength={200} rows={2} onChange={(e) => setContact({ ...contact, hours: e.target.value })} placeholder={'Es. Mar–Sab 10–13 / 15:30–19\nDomenica su appuntamento'} className="mt-1 w-full px-3 py-2 rounded-md border text-sm" />
                  </div>
                )}

                <div>
                  <Label htmlFor="shop-note">Nota per chi viene a trovarti <span className="text-slate-400 font-normal">(facoltativa)</span></Label>
                  <input id="shop-note" value={contact.note} maxLength={200} onChange={(e) => setContact({ ...contact, note: e.target.value })} placeholder="Es. Citofono «Laboratorio», parcheggio in cortile" className="mt-1 w-full h-10 px-3 rounded-md border text-sm" />
                </div>

                {contact.visibility === 'address' && (
                  <p className="text-xs text-slate-500">
                    Al salvataggio calcoliamo la posizione sulla mappa (OpenStreetMap).
                    {shop.contact?.visibility === 'address' && shop.contact?.lat == null && ' Non abbiamo trovato questo indirizzo sulla mappa: controlla via e città, oppure verrà mostrato solo il testo con il link alle indicazioni.'}
                  </p>
                )}
              </>
            )}
            <p className="text-xs text-slate-500 border-t pt-4">
              Telefono ed email non vengono mostrati: i clienti ti scrivono dalla chat di Rarifex, che tiene insieme messaggi e ordini e protegge entrambi.
            </p>
          </Card>
        )}

        {tab === 'spedizioni' && (
          <Card className="p-6 space-y-6" data-testid="shop-policies-editor">
            <div>
              <p className="font-semibold text-slate-800">Le tue tariffe</p>
              <p className="text-xs text-slate-500 mt-0.5">Le stesse usate nel carrello. Nel negozio compaiono nella pagina «Spedizioni e resi» e in ogni scheda prodotto.</p>
              <ul className="mt-3 rounded-lg border divide-y text-sm" data-testid="shop-policies-rates">
                {!shipping ? (
                  <li className="p-3 text-slate-400">Caricamento…</li>
                ) : describeShipping(shipping).map((r) => (
                  <li key={r.name} className="p-3 flex items-center justify-between gap-3">
                    <span className="min-w-0">
                      <span className="text-slate-800">{r.name}</span>
                      <span className="block text-xs text-slate-500">{[r.days, r.free].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span className="font-medium text-slate-900 shrink-0">{r.price}</span>
                  </li>
                ))}
              </ul>
              {shipping && !shipping.rules?.length && <p className="text-xs text-amber-700 mt-2">Non hai ancora tariffe tue: vale quella standard di Rarifex.</p>}
              <Link to="/dashboard?tab=artigiano&section=shipping" className="mt-2 inline-flex items-center gap-1 text-sm text-[#008080] hover:underline">
                Gestisci le tariffe <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="border-t pt-5">
              <Label>Le creazioni disponibili partono…</Label>
              <div className="mt-2 flex flex-wrap gap-2" role="radiogroup">
                {[null, ...DISPATCH_OPTIONS].map((d) => (
                  <button
                    key={d ?? 'none'}
                    type="button"
                    role="radio"
                    aria-checked={(policies.dispatch_days ?? null) === d}
                    onClick={() => setPolicies({ ...policies, dispatch_days: d })}
                    className={`h-9 px-4 rounded-full border text-sm ${(policies.dispatch_days ?? null) === d ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'}`}
                    data-testid={`shop-dispatch-${d ?? 'none'}`}
                  >
                    {d ? `entro ${d} ${d === 1 ? 'giorno' : 'giorni'}` : 'Non indicare'}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1.5">Giorni lavorativi dall'ordine. Per le creazioni su ordinazione vale il tempo indicato in ogni scheda.</p>
            </div>

            {menu.some((m) => m.key === 'su_misura' && m.enabled) && (
              <div>
                <Label>Per un lavoro su misura servono di solito…</Label>
                <div className="mt-2 flex flex-wrap gap-2" role="radiogroup">
                  {[{ id: null, label: 'Non indicare' }, ...CUSTOM_TIMES].map((c) => (
                    <button
                      key={c.id ?? 'none'}
                      type="button"
                      role="radio"
                      aria-checked={(policies.custom_time ?? null) === c.id}
                      onClick={() => setPolicies({ ...policies, custom_time: c.id })}
                      className={`h-9 px-4 rounded-full border text-sm ${(policies.custom_time ?? null) === c.id ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t pt-5 space-y-3">
              <div>
                <Label htmlFor="shop-packaging">Come imballi e spedisci (facoltativo)</Label>
                <textarea
                  id="shop-packaging"
                  value={policies.packaging_note || ''}
                  maxLength={POLICY_TEXT_LIMIT}
                  rows={2}
                  onChange={(e) => setPolicies({ ...policies, packaging_note: e.target.value })}
                  placeholder="Es. Imballo ogni pezzo a mano con carta e cartone riciclati, senza plastica"
                  className="mt-1 w-full px-3 py-2 rounded-md border text-sm"
                  data-testid="shop-packaging-note"
                />
              </div>
              <label className="flex items-center justify-between gap-3 text-sm text-slate-700">
                <span>Confezione regalo su richiesta <span className="block text-xs text-slate-500">I clienti te la chiedono in chat dopo l'ordine</span></span>
                <Switch checked={!!policies.gift_wrap} onCheckedChange={(v) => setPolicies({ ...policies, gift_wrap: v })} data-testid="shop-gift-wrap" />
              </label>
            </div>

            <div className="border-t pt-5">
              <p className="font-semibold text-slate-800">Resi</p>
              <div className="mt-2 rounded-lg bg-slate-50 border p-3 text-xs text-slate-600 space-y-1.5">
                <p className="font-medium text-slate-700">Valgono sempre le regole di Rarifex, mostrate ai clienti nel negozio:</p>
                {RETURN_RULES.slice(0, 3).map((r) => <p key={r.title}>• <strong>{r.title}</strong>: {r.text}</p>)}
              </div>
              <Label htmlFor="shop-returns" className="mt-3 block">Il tuo impegno in più (facoltativo)</Label>
              <textarea
                id="shop-returns"
                value={policies.returns_note || ''}
                maxLength={POLICY_TEXT_LIMIT}
                rows={2}
                onChange={(e) => setPolicies({ ...policies, returns_note: e.target.value })}
                placeholder="Es. Se il colore dal vivo non ti convince, scrivimi: troviamo insieme una soluzione"
                className="mt-1 w-full px-3 py-2 rounded-md border text-sm"
                data-testid="shop-returns-note"
              />
              <p className="text-xs text-slate-500 mt-1">Puoi solo aggiungere garanzie: le regole di Rarifex e i diritti dei clienti non si possono ridurre.</p>
            </div>
          </Card>
        )}

        {tab === 'collezioni' && (
          <ShopCollectionsEditor
            collections={collections}
            onChange={setCollections}
            products={activeProducts}
            menuEnabled={menu.some((m) => m.key === 'collezioni' && m.enabled)}
          />
        )}

        {tab === 'evidenza' && (
          <Card className="p-6">
            <p className="text-sm text-slate-500 mb-3">
              Scegli fino a {MAX_FEATURED} prodotti da mostrare in cima al catalogo, nell'ordine in cui li selezioni ({featured.length}/{MAX_FEATURED}).
            </p>
            {activeProducts.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Pubblica almeno un prodotto per metterlo in evidenza.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="shop-featured-picker">
                {activeProducts.map((p) => {
                  const pos = featured.indexOf(p.id);
                  const img = p.images?.[p.customization_options?.main_image_index || 0] || p.images?.[0];
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleFeatured(p.id)}
                      className={`relative rounded-lg overflow-hidden border-2 text-left ${pos >= 0 ? 'border-slate-800' : 'border-transparent hover:border-slate-300'}`}
                      title={p.title}
                      aria-pressed={pos >= 0}
                    >
                      <div className="aspect-square bg-slate-100">
                        {img && <img src={optimizeImageUrl(img, 200)} alt={p.title} className="w-full h-full object-cover" />}
                      </div>
                      <p className="text-[11px] px-1.5 py-1 truncate">{p.title}</p>
                      {pos >= 0 && (
                        <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center">{pos + 1}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Anteprima dal vivo + salvataggio */}
      <div className="xl:col-span-2 min-w-0">
        <div className="xl:sticky xl:top-4 space-y-3">
          <p className="text-sm text-slate-500">Anteprima della vetrina avanzata</p>
          <ShopPreview
            branding={branding}
            menu={menu}
            featuredProducts={featured.map((id) => activeProducts.find((p) => p.id === id)).filter(Boolean)}
            title={storefrontTitle}
            motto={storefrontMotto}
            coverImage={coverImage}
            sectionName={sectionName}
          />
          <Button onClick={() => save(true)} disabled={saving} className="w-full bg-[#DAA520] text-white hover:bg-[#B8860B]" data-testid="shop-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            {live ? 'Salva modifiche' : 'Salva e pubblica'}
          </Button>
          <div className="flex flex-col gap-2">
            {!live && (
              <Button variant="outline" onClick={() => save(false)} disabled={saving} className="w-full" data-testid="shop-save-draft-btn">
                Salva senza pubblicare
              </Button>
            )}
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="block">
              <Button variant="outline" className="w-full" data-testid="shop-preview-link">
                <ExternalLink className="w-4 h-4 mr-2" /> Anteprima completa
              </Button>
            </a>
          </div>
          <p className="text-xs text-slate-500">L'anteprima completa apre il tuo negozio con l'ultima versione salvata.</p>

          {shareUrl && (
            <div className="mt-4 rounded-xl border p-4 bg-white" data-testid="shop-share-link">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Link2 className="w-4 h-4 text-[#008080]" /> Il link del tuo negozio</p>
              <p className="text-xs text-slate-500 mt-1">
                Usalo su Instagram, nel QR del biglietto da visita o nelle email: chi arriva da qui entra direttamente
                nel tuo negozio e l'acquisto viene attribuito a te.
              </p>
              {shortUrl && (
                <div className="mt-3" data-testid="shop-short-link">
                  <p className="text-xs font-medium text-slate-600">Link breve, il più facile da ricordare e da scrivere</p>
                  <div className="mt-1 flex gap-2">
                    <input readOnly value={shortUrl.replace(/^https:\/\//, '')} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 h-9 px-3 rounded-md border bg-[#008080]/5 border-[#008080]/30 text-sm font-medium text-[#006666]" aria-label="Link breve del negozio" />
                    <Button type="button" variant="outline" size="sm" onClick={() => copyShareUrl(shortUrl)} data-testid="shop-short-copy">
                      <Copy className="w-4 h-4 mr-1" /> Copia
                    </Button>
                  </div>
                  <p className="text-xs font-medium text-slate-600 mt-3">Link completo</p>
                </div>
              )}
              <div className={`${shortUrl ? 'mt-1' : 'mt-3'} flex gap-2`}>
                <input readOnly value={shareUrl} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 h-9 px-3 rounded-md border bg-slate-50 text-xs" aria-label="Link del negozio" />
                <Button type="button" variant="outline" size="sm" onClick={() => copyShareUrl()} data-testid="shop-share-copy">
                  <Copy className="w-4 h-4 mr-1" /> Copia
                </Button>
              </div>
              <ShopQrCode
                url={shortUrl ? `${shortUrl}?via=qr` : `${shareUrl}&via=qr`}
                slug={slug}
                name={storefrontTitle || 'La mia bottega'}
                motto={storefrontMotto}
                logoUrl={branding.logo_url}
                color={getPalette(branding.palette).primaryDark}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Invito al piano Pro per chi non ha accesso alla vetrina avanzata
export function AdvancedStorefrontTeaser() {
  return (
    <Card className="p-5 mt-6 border-2 border-dashed border-[#DAA520]/50 bg-amber-50/40" data-testid="shop-customizer-locked">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-[#DAA520]/15">
          <Lock className="w-5 h-5 text-[#B8860B]" />
        </div>
        <div>
          <h3 className="font-bold">Vetrina avanzata</h3>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Con il piano Pro la tua vetrina diventa il tuo negozio: logo, colori, modello grafico,
            menu su misura e prodotti in evidenza. Stesso indirizzo, nessun link da cambiare.
          </p>
          <Link to="/diventa-artigiano">
            <Button className="mt-4 bg-[#DAA520] text-white hover:bg-[#B8860B]">
              <Crown className="w-4 h-4 mr-2" /> Scopri il piano Pro
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}

// Schizzo del modello grafico nella scelta del layout (con i colori della palette)
function LayoutSketch({ layout, palette }) {
  const P = palette.primary;
  const A = palette.accent;
  const box = (cls, style) => <div className={cls} style={style} />;
  const grid = (n, cls = 'aspect-square rounded-[2px] bg-slate-300', cols = n) => (
    <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {Array.from({ length: n }).map((_, i) => <div key={i} className={cls} />)}
    </div>
  );
  const sketches = {
    classico: (
      <>
        <div className="relative h-10 rounded-[2px] bg-slate-500">
          {box('absolute bottom-1.5 left-1.5 h-1.5 w-1/2 rounded-full bg-white/90')}
          {box('absolute bottom-[3px] left-1.5 h-1 w-6 rounded-full', { background: A })}
        </div>
        {grid(4)}
      </>
    ),
    galleria: (
      <>
        <div className="grid grid-cols-2 gap-1 h-10">
          <div className="flex flex-col justify-center gap-1">{box('h-1.5 w-full rounded-full bg-slate-500')}{box('h-1 w-2/3 rounded-full bg-slate-300')}{box('h-1.5 w-6 rounded-full', { background: P })}</div>
          <div className="grid grid-cols-3 grid-rows-2 gap-0.5">{box('col-span-2 row-span-2 rounded-[2px] bg-slate-400')}{box('rounded-[2px] bg-slate-300')}{box('rounded-[2px] bg-slate-300')}</div>
        </div>
        {grid(2, 'aspect-[4/5] rounded-[2px] bg-slate-300')}
      </>
    ),
    minimale: (
      <>
        <div className="h-10 flex flex-col items-center justify-center gap-1">{box('h-1 w-1/2 bg-slate-500')}{box('h-px w-4', { background: P })}{box('h-0.5 w-1/3 bg-slate-300')}</div>
        {grid(3, 'aspect-square bg-slate-200')}
      </>
    ),
    editoriale: (
      <>
        {box('h-px w-full bg-slate-700')}
        <div className="grid grid-cols-5 gap-1 h-10 items-end">
          <div className="col-span-3 flex flex-col gap-1">{box('h-2.5 w-full bg-slate-600 -skew-x-12')}{box('h-1 w-2/3 bg-slate-300')}</div>
          {box('col-span-2 h-full bg-slate-400')}
        </div>
        {grid(3, 'aspect-[3/4] bg-slate-300')}
      </>
    ),
    contemporaneo: (
      <>
        <div className="h-10 p-1 flex flex-col justify-end gap-0.5" style={{ background: P }}>{box('h-2.5 w-full bg-white')}{box('h-1 w-1/3 bg-white/70')}</div>
        <div className="grid grid-cols-3 border border-slate-800">{[0, 1, 2].map((i) => <div key={i} className={`aspect-[4/3] bg-slate-300 ${i < 2 ? 'border-r border-slate-800' : ''}`} />)}</div>
      </>
    ),
    boutique: (
      <>
        <div className="h-12 bg-slate-800 flex flex-col items-center justify-center gap-1">{box('h-px w-4', { background: A })}{box('h-1.5 w-1/2 bg-white/90')}{box('h-px w-4', { background: A })}</div>
        {grid(3, 'aspect-[4/5] bg-slate-200')}
      </>
    ),
    catalogo: (
      <>
        <div className="h-4 rounded-[2px]" style={{ background: P }} />
        <div className="flex gap-0.5">{[0, 1, 2].map((i) => <div key={i} className="h-1.5 w-5 rounded-full bg-slate-300" />)}</div>
        {grid(10, 'aspect-square rounded-[1px] bg-slate-300 border border-slate-200', 5)}
      </>
    ),
    naturale: (
      <>
        <div className="grid grid-cols-2 gap-1 h-10 items-center">
          <div className="flex flex-col gap-1">{box('h-1.5 w-full rounded-full bg-slate-500')}{box('h-2 w-8 rounded-full', { background: P })}</div>
          <div className="h-10 w-10 justify-self-center bg-slate-400" style={{ borderRadius: '46% 54% 58% 42% / 48% 42% 58% 52%' }} />
        </div>
        {grid(3, 'aspect-square rounded-lg bg-slate-300')}
      </>
    ),
  };
  return (
    <div className="rounded-md border bg-white p-1.5 space-y-1 overflow-hidden" aria-hidden>
      <div className="h-1 rounded-full" style={{ background: palette.primaryDark }} />
      {sketches[layout] || sketches.classico}
    </div>
  );
}

// Anteprima del negozio (modalità negozio) in miniatura: intestazione, apertura e
// griglia cambiano con il modello grafico scelto, come nel sito vero.
function ShopPreview({ branding, menu, featuredProducts, title, motto, coverImage, sectionName }) {
  const st = shopModeStyle(branding.layout);
  const items = normalizeMenu(menu).filter((m) => m.enabled).slice(0, 5);
  const name = title || 'La tua bottega';
  const cover = coverImage ? optimizeImageUrl(coverImage, 600) : null;
  const imgs = featuredProducts.map((p) => p && (p.images?.[p.customization_options?.main_image_index || 0] || p.images?.[0])).filter(Boolean);
  const cols = { galleria: 2, catalogo: 4, contemporaneo: 3 }[st.id] || 3;
  const cells = Array.from({ length: cols === 4 ? 8 : cols === 2 ? 2 : 3 }).map((_, i) => imgs[i] || null);
  const label = (m) => (m.key === 'laboratorio' ? sectionName || 'La bottega' : m.key === 'social' ? 'Contatti' : SHOP_MENU_ITEMS[m.key].label);
  const logo = branding.logo_url ? (
    <img src={optimizeImageUrl(branding.logo_url, 80)} alt="" className="w-6 h-6 rounded object-contain bg-white p-0.5 border" />
  ) : (
    <span className="w-6 h-6 rounded bg-shop-primary text-white text-[10px] font-bold flex items-center justify-center">{name[0].toUpperCase()}</span>
  );
  const overlayHeader = st.transparentHeader && cover;
  const headerBar = (
    <div className={`${overlayHeader ? 'absolute inset-x-0 top-0 z-10 text-white' : st.header} px-3 py-2`}>
      <div className={`flex items-center gap-2 ${st.centeredHeader && !overlayHeader ? 'flex-col' : 'justify-between'}`}>
        <span className={`flex items-center gap-1.5 min-w-0 ${st.centeredHeader && !overlayHeader ? 'flex-col' : ''}`}>
          {logo}<span className={`${st.heading} text-[11px] truncate`}>{name}</span>
        </span>
        <span className="flex gap-2 text-[8px] opacity-80 truncate">
          {items.map((m) => <span key={m.key} className="whitespace-nowrap">{label(m)}</span>)}
        </span>
      </div>
    </div>
  );
  const titleSmall = `${st.heading} text-base leading-tight`;
  const btn = <span className={`inline-block mt-2 px-2 py-1 text-[8px] text-white bg-shop-primary ${st.btn}`}>Scopri le creazioni</span>;
  const heroes = {
    cover: (
      <div className="relative h-36 bg-gradient-to-br from-shop-primary to-shop-primary-dark">
        {cover && <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20" />
        <div className="absolute bottom-2 left-3 right-3 text-white"><p className={titleSmall}>{motto || name}</p></div>
      </div>
    ),
    boutique: (
      <div className="relative h-40 bg-slate-900">
        {cover && <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />}
        <div className="absolute inset-0 pt-8 flex flex-col items-center justify-center text-white text-center px-3">
          <span className="w-6 h-px bg-shop-accent" /><p className={`${titleSmall} my-1.5`}>{name}</p><span className="w-6 h-px bg-shop-accent" />
        </div>
      </div>
    ),
    collage: (
      <div className="grid grid-cols-2 gap-2 p-3 items-center">
        <div><p className={titleSmall}>{name}</p>{btn}</div>
        <div className="grid grid-cols-3 grid-rows-2 gap-1 h-20">
          <div className="col-span-2 row-span-2 rounded bg-slate-300 overflow-hidden">{cover && <img src={cover} alt="" className="w-full h-full object-cover" />}</div>
          <div className="rounded bg-slate-200 overflow-hidden">{cells[0] && <img src={optimizeImageUrl(cells[0], 120)} alt="" className="w-full h-full object-cover" />}</div>
          <div className="rounded bg-slate-200" />
        </div>
      </div>
    ),
    centered: (
      <div className="py-6 text-center px-3"><p className={titleSmall}>{name}</p><span className="block w-6 h-px bg-shop-primary mx-auto my-2" />{motto && <p className="text-[9px] text-slate-500 truncate">{motto}</p>}{btn}</div>
    ),
    editorial: (
      <div className="px-3 pt-2 pb-3">
        <div className="border-b border-slate-900 text-[7px] uppercase tracking-widest text-slate-500 pb-1 flex justify-between"><span>N° 01</span><span>Made in Italy</span></div>
        <div className="grid grid-cols-5 gap-2 pt-2 items-end">
          <div className="col-span-3"><p className={`${st.heading} text-lg leading-none`}>{name}</p>{btn}</div>
          <div className="col-span-2 aspect-[3/4] bg-slate-300 overflow-hidden">{cover && <img src={cover} alt="" className="w-full h-full object-cover" />}</div>
        </div>
      </div>
    ),
    block: (
      <div>
        <div className="bg-shop-primary text-white px-3 py-4"><p className={`${st.heading} text-lg leading-none`}>{name}</p></div>
        <div className="grid grid-cols-3 border-y-2 border-slate-900">{[cover, cells[0], cells[1]].map((src, i) => <div key={i} className={`aspect-[4/3] bg-slate-300 overflow-hidden ${i < 2 ? 'border-r-2 border-slate-900' : ''}`}>{src && <img src={i === 0 ? src : optimizeImageUrl(src, 160)} alt="" className="w-full h-full object-cover" />}</div>)}</div>
      </div>
    ),
    compact: (
      <div className="px-3 pt-2">
        <div className="relative h-12 rounded bg-shop-primary overflow-hidden">{cover && <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />}<p className={`relative ${titleSmall} text-white p-2`}>{name}</p></div>
        <div className="flex gap-1 mt-1.5">{['Tutte', 'Categoria', 'Altra'].map((c, i) => <span key={c} className={`text-[8px] px-2 py-0.5 rounded-full ${i === 0 ? 'bg-shop-primary text-white' : 'bg-white border'}`}>{c}</span>)}</div>
      </div>
    ),
    organic: (
      <div className="grid grid-cols-2 gap-2 p-3 items-center">
        <div><p className={titleSmall}>{name}</p>{btn}</div>
        <div className="aspect-square bg-shop-primary/20 overflow-hidden" style={{ borderRadius: '46% 54% 58% 42% / 48% 42% 58% 52%' }}>{cover && <img src={cover} alt="" className="w-full h-full object-cover" />}</div>
      </div>
    ),
  };
  return (
    <div className={`rounded-xl border overflow-hidden shadow-sm bg-shop-bg ${st.font}`} style={shopThemeStyle(branding)} data-testid="shop-preview" data-preview-layout={st.id}>
      <div className="h-2 bg-shop-primary-dark" />
      <div className="relative">
        {headerBar}
        {heroes[st.hero] || heroes.cover}
      </div>
      <div className="p-3">
        <p className={`text-[9px] uppercase tracking-wider text-shop-primary mb-2 ${st.centered ? 'text-center' : ''}`}>{featuredProducts.length ? 'In evidenza' : 'Le creazioni'}</p>
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {cells.map((src, i) => (
            <div key={i} className={`${st.image} overflow-hidden bg-slate-200 ${st.id === 'contemporaneo' ? 'border-2 border-slate-900' : st.radius === 'rounded-none' ? '' : 'rounded-md'}`}>
              {src && <img src={optimizeImageUrl(src, 200)} alt="" className="w-full h-full object-cover" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
